import { Point, Position } from 'geojson';

import { bearing, Coord, distance, lineString, nearestPointOnLine, point } from '@turf/turf';

/**
 * Returns the nearest point on a polygon or the point itself if it is within the limit.
 *
 * @param lineCoords -  line(s) coords to snap to
 * @param pointCoords - point to snap from
 * @param limit - distance limit to return either the nearest point or the point itself
 * @returns
 *
 * @example
 * var coords = [
 *   [-77.031669, 38.878605],
 *   [-77.029609, 38.881946],
 *   [-77.020339, 38.884084],
 *   [-77.025661, 38.885821],
 *   [-77.021884, 38.889563],
 *   [-77.019824, 38.892368]
 * ];
 * var position= [-77.037076, 38.884017];
 *
 * var snapped = nearestPointOnLineOrPoint(coords, position, 0.5);
 *
 */
export function nearestPointOnLineOrPoint(lineCoords: Position[], pointCoords: Position, limit = 1): Position {
  const nearestPoint = nearestPointOnLines(lineCoords, pointCoords);
  const distance = nearestPoint.properties.dist;

  return distance <= limit ? nearestPoint.geometry.coordinates : pointCoords;
}

/**
 * Returns the nearest point on a polygon.
 *
 * @param lineCoords -  line(s) coords to snap to
 * @param pointCoords - point to snap from
 * @returns
 *
 * @example
 * var coords = [
 *   [-77.031669, 38.878605],
 *   [-77.029609, 38.881946],
 *   [-77.020339, 38.884084],
 *   [-77.025661, 38.885821],
 *   [-77.021884, 38.889563],
 *   [-77.019824, 38.892368]
 * ];
 * var position= [-77.037076, 38.884017];
 *
 * var snapped = nearestPointOnLines(coords, position);
 *
 */
export function nearestPointOnLines(lineCoords: Position[], pointCoords: Position) {
  const lns = lineString(lineCoords);
  const pt = point(pointCoords);
  return nearestPointOnLine(lns, pt, { units: 'kilometers' });
}

/**
 * Returns the distance in kilometers between two points.
 *
 * @param point1 -  first point
 * @param point2 - second point
 * @returns
 *
 * @example
 * var point1 = [-77.031669, 38.878605];
 * var point2 = [-77.029609, 38.881946];
 *
 * var distance = calculateDistanceInKm(point1, point2);
 *
 */
export function calculateDistanceInKm(point1: Point | Coord, point2: Point | Coord): number {
  return distance(point1, point2, { units: 'kilometers' });
}

/**
 * Compass bearing in degrees (0 = north, ±180) from `from` to `to`.
 * TEC-208: replaces `google.maps.geometry.spherical.computeHeading`, which is
 * unavailable on native builds (the Maps JS API no longer loads there).
 */
export function headingBetween(from: { lat: number; lng: number }, to: { lat: number; lng: number }): number {
  return bearing([from.lng, from.lat], [to.lng, to.lat]);
}

/**
 * Distance in meters between two coordinates.
 * TEC-208: replaces `google.maps.geometry.spherical.computeDistanceBetween`.
 */
export function distanceInMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  return distance([a.lng, a.lat], [b.lng, b.lat], { units: 'kilometers' }) * 1000;
}

/**
 * Formats a distance in kilometers into a short human label (e.g. "280 m", "1.2 km").
 * Returns an empty string for non-finite or negative values.
 *
 * @param km - distance in kilometers
 */
export function formatDistanceKm(km: number | null | undefined): string {
  if (km == null || !isFinite(km) || km < 0) {
    return '';
  }
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
}
